import React, { useMemo, useRef, useState } from 'react'
import html2canvas from 'html2canvas'

const MAX_CLASSES = 3
const EXPORT_WIDTH = 1080
const EXPORT_HEIGHT = 1920
const EXPORT_PREVIEW_WIDTH = 360
const EXPORT_PREVIEW_HEIGHT = 640

const DEFAULT_CONTENT = {
  hero: {
    badge: 'New',
    eyebrow: 'Instagram Story Maker',
    title: 'Beautiful class stories in under a minute',
    subtitle:
      'Upload a background or pick a template, add up to three classes, and export a ready-to-post 1080×1920 PNG for Instagram Stories to turbo-charge your marketing.'
  },
  step_1: {
    label: 'Background',
    title: 'Choose a background',
    drop_prompt: 'Drop an image or click to upload',
    drop_hint: 'We recommend 1080×1920 (9:16) vertical images.',
    input_aria: 'Upload background image',
    templates_hint: 'or choose from a Mojo template',
    template_aria: 'Select template %{name}'
  },
  step_2: {
    label: 'Classes',
    title: 'Add up to three class cards',
    add_button: 'Add class',
    class_label: 'Class %{index}',
    remove_button: 'Remove',
    fields: {
      title_label: 'Class title',
      title_placeholder: 'e.g. Sunrise Pilates',
      date_label: 'Date',
      date_placeholder: 'Fri, Jan 24',
      time_label: 'Time',
      time_placeholder: '7:30 AM',
      description_label: 'Description',
      description_placeholder: 'Add a short note about the class.'
    }
  },
  step_3: {
    label: 'Generate',
    title: 'Download as PNG',
    generating: 'Generating...',
    download_button: 'Download story (1080×1920)',
    export_hint: 'Exports instantly on your device. No files leave your browser.',
    success_title: 'PNG ready to post',
    success_link: 'Tap to download again',
    error: 'We could not generate the story. Please try again or use a different image.'
  },
  preview: {
    label: 'Live preview',
    title: 'Scaled to Instagram Stories',
    background_alt: 'Story background',
    footer: 'Preview is scaled down for your screen. Export is always 1080×1920 for Instagram.'
  }
}

const createEmptyClass = () => ({
  id: `class-${Math.random().toString(36).slice(2, 10)}`,
  title: '',
  date: '',
  time: '',
  description: ''
})

const interpolate = (template, values = {}) =>
  String(template || '').replace(/%\{(\w+)\}/g, (_match, key) => values[key] || '')

const cardClass = 'story-maker-card overflow-hidden bg-marketing-paper'
const cardBodyClass = 'px-4 py-5 sm:px-6 lg:px-8'
const inputClass = 'w-full rounded-2xl border border-marketing-line bg-marketing-paper px-4 py-3 text-sm text-marketing-forest shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20'
const labelClass = 'mb-2 block text-sm font-semibold text-marketing-forest'

const StoryMaker = ({ templates = [], content = {} }) => {
  const ui = {
    ...DEFAULT_CONTENT,
    ...content,
    hero: { ...DEFAULT_CONTENT.hero, ...(content.hero || {}) },
    step_1: { ...DEFAULT_CONTENT.step_1, ...(content.step_1 || {}) },
    step_2: {
      ...DEFAULT_CONTENT.step_2,
      ...(content.step_2 || {}),
      fields: {
        ...DEFAULT_CONTENT.step_2.fields,
        ...((content.step_2 || {}).fields || {})
      }
    },
    step_3: { ...DEFAULT_CONTENT.step_3, ...(content.step_3 || {}) },
    preview: { ...DEFAULT_CONTENT.preview, ...(content.preview || {}) }
  }

  const normalizedTemplates = Array.isArray(templates) ? templates : []
  const defaultTemplate = normalizedTemplates[0]

  const [backgroundUrl, setBackgroundUrl] = useState(defaultTemplate?.url || '')
  const [selectedTemplateId, setSelectedTemplateId] = useState(defaultTemplate?.id || null)
  const [classCards, setClassCards] = useState([createEmptyClass()])
  const [isGenerating, setIsGenerating] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [error, setError] = useState(null)

  const fileInputRef = useRef(null)
  const previewRef = useRef(null)

  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template.id)
    setBackgroundUrl(template.url)
    setDownloadUrl(null)
    setError(null)
  }

  const handleFileSelection = (file) => {
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      setBackgroundUrl(event.target?.result || '')
      setSelectedTemplateId(null)
      setDownloadUrl(null)
      setError(null)
    }
    reader.readAsDataURL(file)
  }

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0]
    handleFileSelection(file)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    const file = event.dataTransfer?.files?.[0]
    handleFileSelection(file)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
  }

  const addClassCard = () => {
    if (classCards.length >= MAX_CLASSES) return
    setClassCards((prev) => [...prev, createEmptyClass()])
    setDownloadUrl(null)
  }

  const removeClassCard = (id) => {
    setClassCards((prev) => {
      if (prev.length === 1) return [createEmptyClass()]
      return prev.filter((card) => card.id !== id)
    })
    setDownloadUrl(null)
  }

  const updateCard = (id, field, value) => {
    setClassCards((prev) =>
      prev.map((card) => (card.id === id ? { ...card, [field]: value } : card))
    )
    setDownloadUrl(null)
  }

  const cardsToRender = useMemo(() => {
    const entries = classCards.filter((card) =>
      [card.title, card.date, card.time, card.description].some(Boolean)
    )

    return entries
  }, [classCards])

  const brandLabel = 'usemojo.app'

  const triggerDownload = (dataUrl) => {
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = `mojo-story-${Date.now()}.png`
    link.click()
  }

  const generateStory = async () => {
    if (!previewRef.current) return

    setIsGenerating(true)
    setError(null)

    try {
      if (document.fonts?.ready) {
        await document.fonts.ready
      }

      await new Promise((resolve) => setTimeout(resolve, 120))

      const previewClone = previewRef.current.cloneNode(true)
      previewClone.style.width = `${EXPORT_PREVIEW_WIDTH}px`
      previewClone.style.height = `${EXPORT_PREVIEW_HEIGHT}px`
      previewClone.style.position = 'fixed'
      previewClone.style.left = '-10000px'
      previewClone.style.top = '0'
      previewClone.style.transform = 'none'
      previewClone.style.margin = '0'
      previewClone.style.boxShadow = 'none'
      document.body.appendChild(previewClone)

      const canvas = await html2canvas(previewClone, {
        useCORS: true,
        backgroundColor: null,
        scale: EXPORT_WIDTH / EXPORT_PREVIEW_WIDTH,
        width: EXPORT_PREVIEW_WIDTH,
        height: EXPORT_PREVIEW_HEIGHT,
        windowWidth: EXPORT_PREVIEW_WIDTH,
        windowHeight: EXPORT_PREVIEW_HEIGHT
      })
      previewClone.remove()

      const dataUrl = canvas.toDataURL('image/png')
      setDownloadUrl(dataUrl)
      triggerDownload(dataUrl)
    } catch (err) {
      console.error(err)
      setError(ui.step_3.error)
    } finally {
      setIsGenerating(false)
    }
  }

  const hasCustomBackground = Boolean(backgroundUrl)

  return (
    <section className="story-maker-page py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="story-maker-hero mx-auto mb-10 flex max-w-4xl flex-col items-center text-center lg:mb-14">
          <div className="story-maker-hero__eyebrow mb-3">
            <span className="rounded-full border border-primary/20 bg-marketing-paper px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
              {ui.hero.badge}
            </span>
            <span>{ui.hero.eyebrow}</span>
          </div>
          <h1 className="font-display text-5xl font-normal leading-[0.94] tracking-[-0.045em] text-marketing-forest sm:text-7xl lg:text-[5.5rem]">
            {ui.hero.title}
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-8 text-marketing-muted sm:text-lg">
            {ui.hero.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <div className="space-y-4">
            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex items-center gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                    1
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {ui.step_1.label}
                    </p>
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                      {ui.step_1.title}
                    </h2>
                  </div>
                </div>

                <div
                  className="story-maker-dashed rounded-3xl bg-marketing-ivory px-4 py-8 text-center transition-colors hover:border-primary/40"
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      fileInputRef.current?.click()
                    }
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
                    <i className="fa-solid fa-arrow-up-from-bracket text-xl text-primary" aria-hidden="true" />
                  </div>
                  <p className="font-semibold text-slate-900">{ui.step_1.drop_prompt}</p>
                  <p className="mt-1 text-sm text-slate-500">{ui.step_1.drop_hint}</p>
                  <input
                    ref={fileInputRef}
                    className="hidden"
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    aria-label={ui.step_1.input_aria}
                  />
                </div>

                <p className="my-4 text-center text-sm text-slate-500">{ui.step_1.templates_hint}</p>

                <div className="grid grid-cols-3 gap-3">
                  {normalizedTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={`story-maker-template w-full border-0 bg-transparent p-0 text-left focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 ${
                        selectedTemplateId === template.id ? 'story-maker-template__active' : ''
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                      aria-pressed={selectedTemplateId === template.id}
                      aria-label={interpolate(ui.step_1.template_aria, { name: template.name })}
                    >
                      <span className="story-maker-template__badge">{template.name}</span>
                      <div className="aspect-[9/16]">
                        <img
                          src={template.url}
                          alt={template.name}
                          className="h-full w-full object-cover"
                          crossOrigin="anonymous"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                      2
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        {ui.step_2.label}
                      </p>
                      <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                        {ui.step_2.title}
                      </h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                    onClick={addClassCard}
                    disabled={classCards.length >= MAX_CLASSES}
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    {ui.step_2.add_button}
                  </button>
                </div>

                <div className="space-y-3">
                  {classCards.map((card, index) => (
                    <div className="rounded-3xl border border-marketing-line bg-marketing-ivory p-4 sm:p-5" key={card.id}>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-slate-900">
                          {interpolate(ui.step_2.class_label, { index: index + 1 })}
                        </span>
                        {classCards.length > 1 && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                            onClick={() => removeClassCard(card.id)}
                          >
                            <i className="fa-solid fa-trash-can" aria-hidden="true" />
                            {ui.step_2.remove_button}
                          </button>
                        )}
                      </div>

                      <div className="grid gap-3">
                        <div>
                          <label className={labelClass}>{ui.step_2.fields.title_label}</label>
                          <input
                            type="text"
                            className={inputClass}
                            placeholder={ui.step_2.fields.title_placeholder}
                            value={card.title}
                            onChange={(event) => updateCard(card.id, 'title', event.target.value)}
                          />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className={labelClass}>{ui.step_2.fields.date_label}</label>
                            <input
                              type="text"
                              className={inputClass}
                              placeholder={ui.step_2.fields.date_placeholder}
                              value={card.date}
                              onChange={(event) => updateCard(card.id, 'date', event.target.value)}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>{ui.step_2.fields.time_label}</label>
                            <input
                              type="text"
                              className={inputClass}
                              placeholder={ui.step_2.fields.time_placeholder}
                              value={card.time}
                              onChange={(event) => updateCard(card.id, 'time', event.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <label className={labelClass}>{ui.step_2.fields.description_label}</label>
                          <textarea
                            className={inputClass}
                            rows={2}
                            placeholder={ui.step_2.fields.description_placeholder}
                            value={card.description}
                            onChange={(event) =>
                              updateCard(card.id, 'description', event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex items-center gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                    3
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {ui.step_3.label}
                    </p>
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                      {ui.step_3.title}
                    </h2>
                  </div>
                </div>

                {error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  onClick={generateStory}
                  disabled={isGenerating || !hasCustomBackground}
                >
                  {isGenerating ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                      {ui.step_3.generating}
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-download" aria-hidden="true" />
                      {ui.step_3.download_button}
                    </>
                  )}
                </button>
                <p className="mt-2 text-center text-sm text-slate-500">{ui.step_3.export_hint}</p>

                {downloadUrl && (
                  <div className="mt-3 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" role="alert">
                    <i className="fa-solid fa-circle-check mt-0.5 text-emerald-600" aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="font-semibold text-emerald-900">{ui.step_3.success_title}</div>
                      <a className="mt-1 inline-flex text-sm font-semibold text-primary underline decoration-primary/30 underline-offset-4" href={downloadUrl} download={`mojo-story-${Date.now()}.png`}>
                        {ui.step_3.success_link}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-4 lg:self-start">
            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {ui.preview.label}
                    </p>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                      {ui.preview.title}
                    </h3>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                    1080×1920
                  </span>
                </div>

                <div className="flex justify-center">
                  <div className="story-maker-preview-shell max-w-full">
                    <div className="story-maker-preview-frame">
                      <div ref={previewRef} className="story-maker-preview">
                        <div className="story-maker-preview__background">
                          {backgroundUrl ? (
                            <img src={backgroundUrl} alt={ui.preview.background_alt} crossOrigin="anonymous" />
                          ) : (
                            <div className="flex h-full items-center justify-center bg-slate-200">
                              <i className="fa-regular fa-image text-4xl text-slate-500" aria-hidden="true" />
                            </div>
                          )}
                          <div className="story-maker-preview__overlay" />
                        </div>

                        <div className="story-maker-preview__content flex h-full flex-col justify-between">
                          <div className="story-maker-classes">
                            {cardsToRender.map((card) => (
                              <div className="story-maker-class" key={card.id}>
                                {card.title && (
                                  <p className="story-maker-class__title m-0">{card.title}</p>
                                )}
                                <div className="flex items-start justify-between gap-3">
                                  <p className="story-maker-class__subtitle m-0">
                                    {card.description && <>{card.description}</>}
                                  </p>
                                  {(card.date || card.time) && (
                                    <p className="story-maker-class__meta">
                                      {card.date}
                                      {card.date && card.time && ' / '}
                                      {card.time}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="story-maker-brand mt-4 flex items-center justify-center gap-2">
                            <i className="fa-solid fa-bolt text-primary" aria-hidden="true" />
                            <span>{brandLabel}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-center text-sm text-slate-500">{ui.preview.footer}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default StoryMaker
